import { Navigate, type RouteObject, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import ViewFlowTarget from "@/pages/Brand/Integrations/ViewFlowTraget";
import BrandRoles from "@/pages/Brand/Roles";
import Rule from "@/pages/Brand/Rule";
import BrandUsers from "@/pages/Brand/Users";
import AcceptInvitation from "@/pages/common/AcceptInvitation";
import LoginPage from "@/pages/common/LoginPage";
import NotFound from "@/pages/common/NotFound";
import ResetPassword from "@/pages/common/ResetPassword";
import BrandPage from "@/pages/System/Brand/Index";
import ViewFlowAction from "@/pages/System/Integrations/ViewFlowAction";
import ViewFlowDefinition from "@/pages/System/Integrations/ViewFlowDefinition";
import ViewFlowType from "@/pages/System/Integrations/ViewFlowType";
import RolesPage from "@/pages/System/Role/Index";
import UsersPage from "@/pages/System/Users/Index";
import { useAuthStore } from "@/store/auth.store";

export interface IndexRouteConfig extends Omit<RouteObject, "children"> {
	index: true;
	title?: string;
	isProtected?: boolean;
}

export interface NonIndexRouteConfig extends Omit<RouteObject, "children"> {
	index?: false;
	title?: string;
	isProtected?: boolean;
	children?: Array<IndexRouteConfig | NonIndexRouteConfig>;
}

export type RouteConfig = IndexRouteConfig | NonIndexRouteConfig;

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
	const { accessToken } = useAuthStore();
	if (!accessToken) return <Navigate to="/login" />;
	return <>{children}</>;
};

const SystemRoute = ({ children }: { children: React.ReactNode }) => {
	const { isSuperAdmin, isBrand, brandId } = useAuthStore();
	if (isSuperAdmin !== "true" && isBrand === "true")
		return <Navigate to={`/${brandId}/integrations`} />;
	return <>{children}</>;
};

const BrandRoute = ({ children }: { children: React.ReactNode }) => {
	const { isBrand, brandId: storedBrandId } = useAuthStore();
	const { brandId: urlBrandId } = useParams();

	if (isBrand !== "true" || !storedBrandId || urlBrandId !== storedBrandId) {
		return <Navigate to="/login" replace />;
	}

	return <>{children}</>;
};

export const publicRoutes: RouteConfig[] = [
	{
		path: "/login",
		element: <LoginPage />,
		title: "Login",
		index: false
	},
	{
		path: "/accept-invitation/:token",
		element: <AcceptInvitation />,
		index: false
	},
	{
		path: "/reset-password/:token",
		element: <ResetPassword />,
		index: false
	},
	{
		path: "*",
		element: <NotFound />,
		title: "404 Not Found",
		index: false
	}
];

export const protectedRoutes: RouteObject[] = [
	{
		path: "/",
		element: <Navigate to="/login" replace />
	},
	...publicRoutes,
	{
		path: "/admin",
		element: (
			<ProtectedRoute>
				<SystemRoute>
					<Layout />
				</SystemRoute>
			</ProtectedRoute>
		),
		children: [
			{
				path: "brands",
				element: <BrandPage />
			},
			{
				path: "integrations",
				element: <ViewFlowType />
			},
			{
				path: "integrations/:flowTypeId",
				element: <ViewFlowAction />
			},
			{
				path: "integrations/:flowTypeId/:flowTargetId",
				element: <ViewFlowDefinition />
			},
			{
				path: "users",
				element: <UsersPage />
			},
			{
				path: "roles",
				element: <RolesPage />
			}
		]
	},
	{
		path: "/:brandId",
		element: (
			<ProtectedRoute>
				<BrandRoute>
					<Layout />
				</BrandRoute>
			</ProtectedRoute>
		),
		children: [
			{
				path: "integrations",
				element: <ViewFlowTarget />
			},
			{
				path: "rule",
				element: <Rule />
			},
			{
				path: "users",
				element: <BrandUsers />
			},
			{
				path: "roles",
				element: <BrandRoles />
			}
		]
	}
];
